import { Box } from "@chakra-ui/layout";
import {
  Icon as ChakraIcon,
  type IconProps as ChakraIconProps,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { getSvgSpriteName } from "@src/utils/svg-sprite-name";
import type { IconNames, IconVariants } from "@src/utils/types/iconNames";
import { type FunctionComponent, memo } from "react";

export interface IconProps extends ChakraIconProps {
  name: IconNames;
  variant?: IconVariants;
}
const IconComponent: FunctionComponent<IconProps> = ({
  name,
  variant = "line",
  ...props
}) => {
  const Svg = (props: React.SVGProps<SVGSVGElement>) => (
    <svg {...props}>
      <title>{name}</title>
      <use href={`/${getSvgSpriteName()}#${variant}-${name}`} />
    </svg>
  );

  return <ChakraIcon w="4" h="4" {...props} as={Svg} />;
};
export const Icon = memo(IconComponent);

export const CircleTickIcon: FunctionComponent = () => (
  <Box as="span" w="2rem" h="2rem">
    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <title>
        <Trans>Circle tick icon</Trans>
      </title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10z"
        fill="#BBE5B3"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 15.718a5.714 5.714 0 100-11.429 5.714 5.714 0 000 11.429z"
        fill="#fff"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.286 10A5.72 5.72 0 0110 4.284 5.72 5.72 0 0115.714 10 5.72 5.72 0 0110 15.714a5.72 5.72 0 01-5.714-5.715zm1.142 0c0 2.52 2.051 4.57 4.572 4.57 2.52 0 4.571-2.05 4.571-4.57A4.576 4.576 0 0010 5.428a4.577 4.577 0 00-4.572 4.571z"
        fill="#108043"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.31 8.453l-1.88 1.881-.74-.738a.57.57 0 10-.807.808l1.143 1.142a.57.57 0 00.808 0l2.285-2.285a.57.57 0 10-.808-.808"
        fill="#108043"
      />
    </svg>
  </Box>
);

/** @deprecated */
export const CollapseIcon: FunctionComponent = (props: ChakraIconProps) => (
  <ChakraIcon viewBox="0 0 20 20" {...props}>
    <path
      fill="currentColor"
      d="M19 0a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V1a1 1 0 0 1 1-1h18zM2 7h16V2H2v5zm6 11a1 1 0 0 1 0 2H6a1 1 0 0 1 0-2h2zm6 0h-2a1 1 0 0 0 0 2h2a1 1 0 0 0 0-2zm5-1a1 1 0 0 0-1 1 1 1 0 0 0 0 2h1a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1zM2 18a1 1 0 0 0-2 0v1a1 1 0 0 0 1 1h1a1 1 0 0 0 0-2zm-1-3a1 1 0 0 0 1-1v-2a1 1 0 0 0-2 0v2a1 1 0 0 0 1 1zm18-4a1 1 0 0 0-1 1v2a1 1 0 0 0 2 0v-2a1 1 0 0 0-1-1z"
    />
  </ChakraIcon>
);

export const GoogleDriveIcon: FunctionComponent<ChakraIconProps> = (props) => (
  <ChakraIcon viewBox="0 0 48 48" {...props}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
      <title>
        <Trans>Google Drive icon</Trans>
      </title>
      <path
        fill="#1e88e5"
        d="M38.59 39c-.535.93-.298 1.68-1.195 2.197A6.004 6.004 0 0 1 34.39 42H13.61a6.015 6.015 0 0 1-3.004-.802C9.708 40.681 9.945 39.93 9.41 39l7.67-9h13.84l7.67 9z"
      />
      <path
        fill="#fbc02d"
        d="M27.463 6.999c1.073-.002 2.104-.716 3.001-.198a6.008 6.008 0 0 1 2.197 2.201l10.39 17.996c.537.93.807 1.967.808 3.002.001 1.037-1.267 2.073-1.806 3.001l-11.127-3.005-6.924-11.993 3.461-11.004z"
      />
      <path
        fill="#e53935"
        d="M43.86 30c0 1.04-.27 2.07-.81 3l-3.67 6.35a6.05 6.05 0 0 1-1.99 1.85L30.92 30h12.94z"
      />
      <path
        fill="#4caf50"
        d="M5.947 33.001c-.538-.928-1.806-1.964-1.806-3a6.011 6.011 0 0 1 .808-3.004l10.39-17.996a6.01 6.01 0 0 1 2.196-2.2c.897-.519 1.929.195 3.002.197l3.459 11.009-6.922 11.989-11.127 3.005z"
      />
      <path
        fill="#1565c0"
        d="m17.08 30-6.47 11.2a6.05 6.05 0 0 1-1.99-1.85L4.95 33c-.54-.93-.81-1.96-.81-3h12.94z"
      />
      <path
        fill="#2e7d32"
        d="M30.46 6.8 24 18 17.53 6.8c.78-.45 1.66-.73 2.6-.79L27.46 6c1.08 0 2.11.28 3 .8z"
      />
    </svg>
  </ChakraIcon>
);

export const GoogleCalendarIcon: FunctionComponent<ChakraIconProps> = (
  props,
) => (
  <ChakraIcon viewBox="0 0 48 48" {...props}>
    <svg
      version="1.1"
      id="Livello_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      viewBox="0 0 200 200"
      enableBackground="new 0 0 200 200"
      xmlSpace="preserve"
    >
      <title>
        <Trans>Google Calendar icon</Trans>
      </title>
      <g>
        <g transform="translate(3.75 3.75)">
          <path
            fill="#FFFFFF"
            d="M148.882,43.618l-47.368-5.263l-57.895,5.263L38.355,96.25l5.263,52.632l52.632,6.579l52.632-6.579
			l5.263-53.947L148.882,43.618z"
          />
          <path
            fill="#1A73E8"
            d="M65.211,125.276c-3.934-2.658-6.658-6.539-8.145-11.671l9.132-3.763c0.829,3.158,2.276,5.605,4.342,7.342
			c2.053,1.737,4.553,2.592,7.474,2.592c2.987,0,5.553-0.908,7.697-2.724s3.224-4.132,3.224-6.934c0-2.868-1.132-5.211-3.395-7.026
			s-5.105-2.724-8.5-2.724h-5.276v-9.039H76.5c2.921,0,5.382-0.789,7.382-2.368c2-1.579,3-3.737,3-6.487
			c0-2.447-0.895-4.395-2.684-5.855s-4.053-2.197-6.803-2.197c-2.684,0-4.816,0.711-6.395,2.145s-2.724,3.197-3.447,5.276
			l-9.039-3.763c1.197-3.395,3.395-6.395,6.618-8.987c3.224-2.592,7.342-3.895,12.342-3.895c3.697,0,7.026,0.711,9.974,2.145
			c2.947,1.434,5.263,3.421,6.934,5.947c1.671,2.539,2.5,5.382,2.5,8.539c0,3.224-0.776,5.947-2.329,8.184
			c-1.553,2.237-3.461,3.947-5.724,5.145v0.539c2.987,1.25,5.421,3.158,7.342,5.724c1.908,2.566,2.868,5.632,2.868,9.211
			s-0.908,6.776-2.724,9.579c-1.816,2.803-4.329,5.013-7.513,6.618c-3.197,1.605-6.789,2.421-10.776,2.421
			C73.408,129.263,69.145,127.934,65.211,125.276z"
          />
          <path
            fill="#1A73E8"
            d="M121.25,79.961l-9.974,7.25l-5.013-7.605l17.987-12.974h6.895v61.197h-9.895L121.25,79.961z"
          />
          <path
            fill="#EA4335"
            d="M148.882,196.25l47.368-47.368l-23.684-10.526l-23.684,10.526l-10.526,23.684L148.882,196.25z"
          />
          <path
            fill="#34A853"
            d="M33.092,172.566l10.526,23.684h105.263v-47.368H43.618L33.092,172.566z"
          />
          <path
            fill="#4285F4"
            d="M12.039-3.75C3.316-3.75-3.75,3.316-3.75,12.039v136.842l23.684,10.526l23.684-10.526V43.618h105.263
			l10.526-23.684L148.882-3.75H12.039z"
          />
          <path
            fill="#188038"
            d="M-3.75,148.882v31.579c0,8.724,7.066,15.789,15.789,15.789h31.579v-47.368H-3.75z"
          />
          <path
            fill="#FBBC04"
            d="M148.882,43.618v105.263h47.368V43.618l-23.684-10.526L148.882,43.618z"
          />
          <path
            fill="#1967D2"
            d="M196.25,43.618V12.039c0-8.724-7.066-15.789-15.789-15.789h-31.579v47.368H196.25z"
          />
        </g>
      </g>
    </svg>
  </ChakraIcon>
);

export const OutlookCalendarIcon: FunctionComponent<ChakraIconProps> = (
  props,
) => (
  <ChakraIcon viewBox="0 0 48 48" {...props}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      width="48"
      height="48"
      viewBox="0 0 48 48"
    >
      <title>
        <Trans>Outlook Calendar icon</Trans>
      </title>
      <path
        fill="#1976d2"
        d="M28,13h14.533C43.343,13,44,13.657,44,14.467v19.066C44,34.343,43.343,35,42.533,35H28V13z"
      ></path>
      <rect width="14" height="15.542" x="28" y="17.958" fill="#fff"></rect>
      <polygon fill="#1976d2" points="27,44 4,39.5 4,8.5 27,4"></polygon>
      <path
        fill="#fff"
        d="M15.25,16.5c-3.176,0-5.75,3.358-5.75,7.5s2.574,7.5,5.75,7.5S21,28.142,21,24	S18.426,16.5,15.25,16.5z M15,28.5c-1.657,0-3-2.015-3-4.5s1.343-4.5,3-4.5s3,2.015,3,4.5S16.657,28.5,15,28.5z"
      ></path>
      <rect
        width="2.7"
        height="2.9"
        x="28.047"
        y="29.737"
        fill="#1976d2"
      ></rect>
      <rect
        width="2.7"
        height="2.9"
        x="31.448"
        y="29.737"
        fill="#1976d2"
      ></rect>
      <rect
        width="2.7"
        height="2.9"
        x="34.849"
        y="29.737"
        fill="#1976d2"
      ></rect>
      <rect
        width="2.7"
        height="2.9"
        x="28.047"
        y="26.159"
        fill="#1976d2"
      ></rect>
      <rect
        width="2.7"
        height="2.9"
        x="31.448"
        y="26.159"
        fill="#1976d2"
      ></rect>
      <rect
        width="2.7"
        height="2.9"
        x="34.849"
        y="26.159"
        fill="#1976d2"
      ></rect>
      <rect width="2.7" height="2.9" x="38.25" y="26.159" fill="#1976d2"></rect>
      <rect
        width="2.7"
        height="2.9"
        x="28.047"
        y="22.706"
        fill="#1976d2"
      ></rect>
      <rect
        width="2.7"
        height="2.9"
        x="31.448"
        y="22.706"
        fill="#1976d2"
      ></rect>
      <rect
        width="2.7"
        height="2.9"
        x="34.849"
        y="22.706"
        fill="#1976d2"
      ></rect>
      <rect width="2.7" height="2.9" x="38.25" y="22.706" fill="#1976d2"></rect>
      <rect
        width="2.7"
        height="2.9"
        x="31.448"
        y="19.112"
        fill="#1976d2"
      ></rect>
      <rect
        width="2.7"
        height="2.9"
        x="34.849"
        y="19.112"
        fill="#1976d2"
      ></rect>
      <rect width="2.7" height="2.9" x="38.25" y="19.112" fill="#1976d2"></rect>
    </svg>
  </ChakraIcon>
);

export const SharepointIcon: FunctionComponent<ChakraIconProps> = (props) => (
  <ChakraIcon viewBox="0 0 48 48" {...props}>
    <linearGradient
      id="a"
      x1="16.145"
      x2="26.377"
      y1="6.428"
      y2="20.715"
      gradientUnits="userSpaceOnUse"
    >
      <stop offset="0" stopColor="#048489" />
      <stop offset="1" stopColor="#03676a" />
    </linearGradient>
    <path
      fill="url(#a)"
      d="M23 4c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12S29.627 4 23 4z"
    />
    <linearGradient
      id="b"
      x1="27.122"
      x2="41.023"
      y1="18.616"
      y2="35.799"
      gradientUnits="userSpaceOnUse"
    >
      <stop offset="0" stopColor="#18939a" />
      <stop offset=".41" stopColor="#168c93" />
      <stop offset="1" stopColor="#117981" />
    </linearGradient>
    <path
      fill="url(#b)"
      d="M33.5 16C27.701 16 23 20.701 23 26.5S27.701 37 33.5 37 44 32.299 44 26.5 39.299 16 33.5 16z"
    />
    <linearGradient
      id="c"
      x1="16"
      x2="32"
      y1="36"
      y2="36"
      gradientUnits="userSpaceOnUse"
    >
      <stop offset="0" stopColor="#37c6d0" />
      <stop offset="1" stopColor="#37c6d0" />
    </linearGradient>
    <path fill="url(#c)" d="M24 28a8 8 0 1 0 0 16 8 8 0 0 0 0-16z" />
    <path
      d="M22.319 13H11.393A12.017 12.017 0 0 0 11 16c0 6.627 5.373 12 12 12 .04 0 .078-.006.118-.006l.009.056A8 8 0 0 0 16 36c0 .339.028.672.069 1h6.25A3.68 3.68 0 0 0 26 33.319V16.681A3.68 3.68 0 0 0 22.319 13z"
      opacity=".05"
    />
    <path
      d="M16 36h6.213a3.12 3.12 0 0 0 3.121-3.121V16.454a3.12 3.12 0 0 0-3.121-3.121H11.308A11.962 11.962 0 0 0 11 16c0 6.627 5.373 12 12 12 .04 0 .078-.006.118-.006l.009.056A8 8 0 0 0 16 36z"
      opacity=".07"
    />
    <path
      d="M22.106 13.667H11.231A12.033 12.033 0 0 0 11 16c0 6.627 5.373 12 12 12 .04 0 .078-.006.118-.006l.009.056A7.997 7.997 0 0 0 16.07 35h6.037a2.56 2.56 0 0 0 2.56-2.56V16.227a2.56 2.56 0 0 0-2.561-2.56z"
      opacity=".09"
    />
    <linearGradient
      id="d"
      x1="4.586"
      x2="23.414"
      y1="14.586"
      y2="33.414"
      gradientUnits="userSpaceOnUse"
    >
      <stop offset="0" stopColor="#058f92" />
      <stop offset="1" stopColor="#027074" />
    </linearGradient>
    <path
      fill="url(#d)"
      d="M22 34H6a2 2 0 0 1-2-2V16a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2z"
    />
    <path
      fill="#fff"
      d="M10.432 28.75v-2.298c.426.349 2.391 1.048 2.9 1.048.299 0 2.138.088 2.138-1.041 0-1.633-5.082-1.494-5.082-4.725 0-.536.066-3.059 4.133-3.059 1.041 0 2.271.261 2.628.395v2.147c-.176-.12-.89-.718-2.496-.718-1.877 0-2.04.883-2.04 1.041 0 1.359 4.998 1.544 4.998 4.711 0 3.172-3.614 3.074-4.177 3.074-.577 0-2.62-.383-3.002-.575z"
    />
  </ChakraIcon>
);

type SetIconProps = Omit<IconProps, "name">;
export const BudgetIcon = (props: SetIconProps) => {
  return <Icon {...props} name="budget-02" />;
};

export const TrackedTimeIcon = (props: SetIconProps) => {
  return <Icon {...props} name="tracked-time" />;
};

export const InfoIcon = (props: SetIconProps) => {
  return <Icon color="orange.300" {...props} name="info-new" />;
};

export const WarningIcon = (props: SetIconProps) => {
  return <Icon color="orange.300" {...props} name="warning-new" />;
};

export const ErrorIcon = (props: SetIconProps) => {
  return <Icon color="red.500" {...props} name="error-new" />;
};

export const TimeAllocationIcon = (props: SetIconProps) => {
  return <Icon {...props} name="time-allocation" />;
};
